import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Package, 
  User, 
  Calendar,
  Tag,
  MapPin,
  Camera,
  Upload,
  CheckCircle,
  Clock,
  Building
} from 'lucide-react';
import { loansService } from '../services/loansService';
import api from '../services/api';
import LoanForm from '../components/LoanForm';

const LoanDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    load();
    loadProducts();
  }, [id]);
  
  const loadProducts = async () => {
    try {
      const response = await api.get('/products');
      if (response.data && response.data.success) {
        const mappedProducts = response.data.data.map(p => ({
          ...p,
          stock: p.current_stock || 0
        }));
        setProducts(mappedProducts);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const load = async () => {
    try {
      const r = await loansService.getById(id);
      if (r.success) setLoan(r.data);
      const imgs = await api.get(`/loans-images/${id}`);
      if (imgs.data && imgs.data.success) setImages(imgs.data.data);
      else setImages(imgs.data || []);
      // debug: show returned image objects
      console.debug('Loan images loaded:', imgs.data && imgs.data.data ? imgs.data.data : imgs.data);
    } catch (err) {
      console.error(err);
      alert('Error cargando detalles');
    }
  };

  const handleFile = async (e, type='out') => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('image', file);
    setUploading(true);
    try {
      const resp = await api.post(`/loans-images/${id}/${type}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (resp.data && resp.data.success) {
        await load();
      } else alert('Upload failed');
    } catch (err) {
      console.error(err);
      alert('Error subiendo imagen');
    } finally { setUploading(false); }
  };

  const handleDelete = async (img) => {
    if (!window.confirm('Eliminar imagen?')) return;
    try {
      await api.delete(`/loans-images/${id}/${img.type}/${img.filename}`);
      await load();
    } catch (err) {
      console.error(err);
      alert('Error eliminando imagen');
    }
  };

  // open image in modal viewer
  const openImage = (url) => {
    setViewerImage(url);
  };
  const closeImage = () => setViewerImage(null);
  
  const handleSaveLoan = async (formData) => {
    try {
      console.log('📑 Datos del formulario a enviar:', formData);
      const response = await loansService.update(id, formData);
      console.log('📑 Respuesta del servidor:', response);
      if (response.success) {
        // Recargar los datos actualizados
        await load();
        setShowEditModal(false);
      } else {
        alert('Error al actualizar el préstamo: ' + (response.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error updating loan:', error);
      alert('Error al actualizar el préstamo: ' + (error.message || 'Error desconocido'));
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!loan) return <div className="p-8">Cargando...</div>;

  const getLoanStatusClass = () => {
    if (loan.returned) return "bg-green-100 text-green-800";
    return loan.expected_return_date && new Date(loan.expected_return_date) < new Date() 
      ? "bg-red-100 text-red-800" 
      : "bg-yellow-100 text-yellow-800";
  };

  const getLoanStatus = () => {
    if (loan.returned) return "Devuelto";
    return loan.expected_return_date && new Date(loan.expected_return_date) < new Date() 
      ? "Vencido" 
      : "Pendiente";
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No especificado';
    const date = new Date(dateString);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() + userTimezoneOffset);
    return localDate.toLocaleDateString('es-MX');
  };

  return (
    <motion.div 
      className="min-h-[calc(100vh-180px)] px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Cabecera */}
      <div className="flex justify-between items-center mb-8">
        <motion.button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Préstamos
        </motion.button>
        
        {!loan.returned && (
          <motion.button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
              <path d="m15 5 4 4"></path>
            </svg>
            Editar Préstamo
          </motion.button>
        )}
      </div>

      {/* Título con gradiente y animación */}
      <motion.h2 
        className="text-4xl font-extrabold text-gray-800 mb-8 text-center bg-gradient-to-r from-indigo-600 to-teal-600 bg-clip-text text-transparent"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        Detalle del Préstamo #{loan.id}
      </motion.h2>

      {/* Tarjeta principal con detalles */}
      <motion.div 
        className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Columna izquierda */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-6 h-6 text-indigo-600" />
              <h3 className="text-xl font-semibold text-gray-800">Información del Material</h3>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="mb-3">
                <span className="text-gray-500 text-sm">Material:</span>
                <div className="font-medium text-lg cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1" onClick={() => navigate(`/inventory/product/${loan.product_id}`)}>
                  {loan.product_name}
                  <span className="text-xs text-blue-600">(Ver ficha técnica)</span>
                </div>
              </div>
              <div className="mb-3">
                <span className="text-gray-500 text-sm">SKU / Código:</span>
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-indigo-500" />
                  <span className="font-medium">{loan.product_sku || loan.product_code || '-'}</span>
                </div>
              </div>
              <div className="mb-3">
                <span className="text-gray-500 text-sm">Cantidad:</span>
                <div className="font-medium">{loan.quantity || 1} unidad(es)</div>
              </div>
              <div className="mb-3">
                <span className="text-gray-500 text-sm">Propósito del préstamo:</span>
                <div className="font-medium italic">{loan.purpose || 'No especificado'}</div>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Número de requisición:</span>
                <div className="font-medium">{loan.requisition || 'No especificado'}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-6 h-6 text-indigo-600" />
              <h3 className="text-xl font-semibold text-gray-800">Fechas</h3>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="mb-3">
                <span className="text-gray-500 text-sm">Fecha de préstamo:</span>
                <div className="font-medium">{formatDate(loan.loan_date)}</div>
              </div>
              <div className="mb-3">
                <span className="text-gray-500 text-sm">Fecha de devolución esperada:</span>
                <div className={`font-medium ${!loan.returned && loan.expected_return_date && new Date(loan.expected_return_date) < new Date() ? 'text-red-600' : ''}`}>
                  {formatDate(loan.expected_return_date)}
                </div>
              </div>
              {loan.returned && (
                <div>
                  <span className="text-gray-500 text-sm">Fecha de devolución real:</span>
                  <div className="font-medium text-green-600">{formatDate(loan.return_date)}</div>
                </div>
              )}
            </div>
          </div>
          
          {/* Columna derecha */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <User className="w-6 h-6 text-indigo-600" />
              <h3 className="text-xl font-semibold text-gray-800">Información del Prestatario</h3>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="mb-3">
                <span className="text-gray-500 text-sm">Nombre:</span>
                <div className="font-medium text-lg">{loan.borrower_name}</div>
              </div>
              {loan.borrower_contact && (
                <div className="mb-3">
                  <span className="text-gray-500 text-sm">Contacto:</span>
                  <div className="font-medium">{loan.borrower_contact}</div>
                </div>
              )}
              <div>
                <span className="text-gray-500 text-sm">Taller / Departamento:</span>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-indigo-500" />
                  <span className="font-medium">{loan.workshop || loan.department || loan.dept || loan.taller || '-'}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-6 h-6 text-indigo-600" />
              <h3 className="text-xl font-semibold text-gray-800">Estado del Préstamo</h3>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex flex-col items-center justify-center py-4">
                <div className={`px-4 py-2 rounded-full ${getLoanStatusClass()} font-semibold text-center flex items-center gap-2 mb-3`}>
                  {loan.returned ? <CheckCircle className="w-5 h-5" /> : (
                    loan.expected_return_date && new Date(loan.expected_return_date) < new Date() ? 
                      <Clock className="w-5 h-5" /> : <Clock className="w-5 h-5" />
                  )}
                  {getLoanStatus()}
                </div>
                {!loan.returned && (
                  <p className="text-center text-gray-500 text-sm">
                    {loan.expected_return_date && new Date(loan.expected_return_date) < new Date() 
                      ? "Este préstamo está vencido y debe ser devuelto"
                      : "El material aún está en posesión del prestatario"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Sección de fotos */}
      <h3 className="text-2xl font-bold mb-6 text-gray-800">Registro Fotográfico</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-lg">Fotos al prestar</h3>
            </div>
            <label className="cursor-pointer flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors">
              <Upload className="w-4 h-4" />
              <span>Subir foto</span>
              <input type="file" accept="image/*" onChange={(e) => handleFile(e, 'out')} disabled={uploading} className="hidden" />
            </label>
          </div>
          
          {images.filter(i=>i.type==='out').length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {images.filter(i=>i.type==='out').map(img => (
                <motion.div 
                  key={img.filename} 
                  className="bg-gray-50 rounded-lg overflow-hidden shadow"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="relative h-40">
                    {(() => {
                      const raw = img.image_url || img.imageUrl || '';
                      const apiBase = api.defaults?.baseURL ? api.defaults.baseURL.replace(/\/api$/, '') : window.location.origin;
                      const full = raw.startsWith('http') ? raw : `${apiBase}${raw}`;
                      return (
                        <img
                          src={full}
                          alt=""
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => openImage(full)}
                          onError={(e) => { e.currentTarget.style.opacity = 0.4; e.currentTarget.style.objectFit = 'contain'; e.currentTarget.onerror = null; }}
                        />
                      );
                    })()}
                  </div>
                  <div className="p-2 flex justify-end">
                    <button 
                      onClick={() => handleDelete(img)} 
                      className="text-xs text-white bg-red-500 hover:bg-red-600 transition-colors px-2 py-1 rounded"
                    >
                      Eliminar
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No hay fotos al momento del préstamo</p>
            </div>
          )}
        </motion.div>

        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-lg">Fotos al regresar</h3>
            </div>
            <label className={`cursor-pointer flex items-center gap-1 px-3 py-1.5 rounded-lg ${!loan.returned ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors`}>
              <Upload className="w-4 h-4" />
              <span>Subir foto</span>
              <input type="file" accept="image/*" onChange={(e) => handleFile(e, 'return')} disabled={uploading || !loan.returned} className="hidden" />
            </label>
          </div>
          
          {images.filter(i=>i.type==='return').length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {images.filter(i=>i.type==='return').map(img => (
                <motion.div 
                  key={img.filename} 
                  className="bg-gray-50 rounded-lg overflow-hidden shadow"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="relative h-40">
                    {(() => {
                      const raw = img.image_url || img.imageUrl || '';
                      const apiBase = api.defaults?.baseURL ? api.defaults.baseURL.replace(/\/api$/, '') : window.location.origin;
                      const full = raw.startsWith('http') ? raw : `${apiBase}${raw}`;
                      return (
                        <img
                          src={full}
                          alt=""
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => openImage(full)}
                          onError={(e) => { e.currentTarget.style.opacity = 0.4; e.currentTarget.style.objectFit = 'contain'; e.currentTarget.onerror = null; }}
                        />
                      );
                    })()}
                  </div>
                  <div className="p-2 flex justify-end">
                    <button 
                      onClick={() => handleDelete(img)} 
                      className="text-xs text-white bg-red-500 hover:bg-red-600 transition-colors px-2 py-1 rounded"
                    >
                      Eliminar
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>{loan.returned ? "No hay fotos de la devolución" : "El material aún no ha sido devuelto"}</p>
            </div>
          )}
        </motion.div>
      </div>
      {/* Image viewer modal */}
      <AnimatePresence>
        {viewerImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeImage}
          >
            <motion.div
              className="max-w-4xl max-h-[90vh]"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={closeImage} className="absolute top-6 right-6 z-60 text-white bg-black/40 rounded-full p-2">X</button>
              <img src={viewerImage} alt="viewer" className="max-w-full max-h-[90vh] rounded-lg shadow-lg" />
            </motion.div>
          </motion.div>
        )}
        
        {/* Edit Loan Modal */}
        {showEditModal && (
          <LoanForm 
            currentLoan={loan}
            onSave={handleSaveLoan}
            onCancel={() => setShowEditModal(false)}
            products={products}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LoanDetailPage;

/* Modal viewer insertion: append before export if needed in some setups - however
   file already exports default, so inserting modal markup inside component is necessary.
   The modal code was injected earlier via state 'viewerImage'. */
